import React from 'react';
import { approveKycRequest, getKycQueue, rejectKycRequest } from '../../services/admin';

const KYC_PENDING_COUNT_EVENT = 'responseops.kyc-pending-count';

type KycRecord = {
  id: string;
  userId: string;
  initials: string;
  name: string;
  phone: string;
  role: string;
  document: string;
  nationalId: string;
  licenceNumber: string;
  licenceExpiryDate: string;
  companyName: string;
  companyRegNumber: string;
  rejectionReason: string;
  submitted: string;
  submittedAt: string;
  status: string;
  reviewedAt: string;
  reviewedBy: string;
  documents: KycDocument[];
};

type KycDocument = {
  id: string;
  label: string;
  url: string;
  mimeType: string;
};

const demoQueue: KycRecord[] = [
  createDemoRecord('demo-401', 'Olumide Adebayo', '+234 801 234 5678', 'driver', 'LAG-DR-401', '2 hours ago'),
  createDemoRecord('demo-402', 'Aisha Bello', '+234 802 345 6789', 'driver', 'LAG-DR-402', '5 hours ago'),
  createDemoRecord('demo-403', 'Emeka Onyeka', '+234 803 456 7890', 'fleet operator', 'OP-2024-018', 'yesterday'),
  createDemoRecord('demo-404', 'Yetunde Lawal', '+234 804 567 8901', 'driver', 'LAG-DR-403', 'yesterday'),
];

export function KycQueue({ searchQuery = '' }: { searchQuery?: string }) {
  const [queue, setQueue] = React.useState<KycRecord[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [activeRequestId, setActiveRequestId] = React.useState('');
  const [viewing, setViewing] = React.useState<KycRecord | null>(null);
  const [viewingDocument, setViewingDocument] = React.useState<KycDocument | null>(null);
  const [rejecting, setRejecting] = React.useState<KycRecord | null>(null);
  const [rejectionReason, setRejectionReason] = React.useState('');
  const [message, setMessage] = React.useState('');

  React.useEffect(() => {
    let isActive = true;

    getKycQueue()
      .then((response) => {
        if (isActive) {
          const normalizedQueue = normalizeQueue(response);
          setQueue(normalizedQueue);
          publishPendingCount(normalizedQueue);
        }
      })
      .catch(() => {
        if (isActive) {
          setQueue(demoQueue);
          publishPendingCount(demoQueue);
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, []);

  React.useEffect(() => {
    if (!rejecting && !viewing && !viewingDocument) {
      return undefined;
    }

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setRejecting(null);
        setViewing(null);
        setViewingDocument(null);
      }
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [rejecting, viewing, viewingDocument]);

  const updateRequest = async (request: KycRecord, decision: 'approved' | 'rejected', reason = '') => {
    setActiveRequestId(request.id);
    setMessage('');

    try {
      if (!request.id.startsWith('demo-')) {
        if (decision === 'approved') {
          await approveKycRequest(request.userId);
        } else {
          await rejectKycRequest(request.userId, reason);
        }
      }

      setQueue((current) => {
        const nextQueue = current.map((item) => item.id === request.id
          ? {
            ...item,
            status: decision,
            rejectionReason: decision === 'rejected' ? reason : item.rejectionReason,
          }
          : item);
        publishPendingCount(nextQueue);
        return nextQueue;
      });
      setViewing(null);
      setViewingDocument(null);
      setRejecting(null);
      setRejectionReason('');
      setMessage(`${request.name}'s KYC request was ${decision}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : `Unable to mark request as ${decision}`);
    } finally {
      setActiveRequestId('');
    }
  };

  const pendingCount = queue.filter((item) => item.status === 'pending').length;
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const visibleQueue = normalizedSearch
    ? queue.filter((request) => [
      request.name,
      request.phone,
      request.role,
      request.document,
      request.nationalId,
      request.licenceNumber,
      request.companyName,
      request.companyRegNumber,
      request.status,
      request.submitted,
    ].some((value) => value.toLowerCase().includes(normalizedSearch)))
    : queue;

  return (
    <div className="kyc-page">
      <div className="section-head kyc-head">
        <h2>KYC review queue</h2>
        <span className="pill warn">{pendingCount} pending</span>
      </div>

      {message ? <div className="kyc-message" role="status">{message}</div> : null}

      <div className="data-table kyc-table">
        <table>
          <thead>
            <tr>
              <th>Applicant</th>
              <th>Role</th>
              <th>Document</th>
              <th>Submitted</th>
              <th>Status</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td className="kyc-table-state" colSpan={6}>Loading KYC requests...</td></tr>
            ) : visibleQueue.length ? visibleQueue.map((request) => (
              <tr key={request.id}>
                <td data-label="Applicant">
                  <div className="row-cell">
                    <span className="row-avatar">{request.initials}</span>
                    <span className="row-info">
                      <strong>{request.name}</strong>
                      <span>{request.phone}</span>
                    </span>
                  </div>
                </td>
                <td data-label="Role"><span className="pill info">{request.role}</span></td>
                <td data-label="Document"><span className="kyc-document">{request.document}</span></td>
                <td data-label="Submitted">{request.submitted}</td>
                <td data-label="Status"><span className={`pill ${getStatusTone(request.status)}`}>{request.status}</span></td>
                <td>
                  <div className="kyc-actions">
                    <button
                      aria-label={`View ${request.name}'s KYC submission`}
                      className="btn-icon kyc-view-btn"
                      title="View KYC submission"
                      type="button"
                      onClick={() => setViewing(request)}
                    >
                      <EyeIcon />
                    </button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr>
                <td className="kyc-table-state" colSpan={6}>
                  {normalizedSearch ? `No KYC requests match "${searchQuery.trim()}".` : 'No KYC requests are waiting for review.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {viewing ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setViewing(null)}>
          <section
            aria-labelledby="view-kyc-title"
            aria-modal="true"
            className="signup-dialog kyc-details-dialog"
            role="dialog"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="signup-dialog-head">
              <div>
                <span className="row-avatar">{viewing.initials}</span>
                <div>
                  <h2 id="view-kyc-title">{viewing.name}</h2>
                  <span className="pill info">{viewing.role}</span>
                </div>
              </div>
              <button className="btn-icon borderless" type="button" aria-label="Close KYC details" onClick={() => setViewing(null)}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <h3 className="kyc-details-heading">Submitted documents</h3>
            {viewing.documents.length ? (
              <div className="kyc-document-list">
                {viewing.documents.map((document) => (
                  <article className="kyc-document-card" key={document.id}>
                    <span className="kyc-document-icon" aria-hidden="true">
                      <DocumentIcon />
                    </span>
                    <div>
                      <strong>{document.label}</strong>
                      <span>{getDocumentTypeLabel(document)}</span>
                    </div>
                    <button className="btn btn-outline btn-sm" type="button" onClick={() => setViewingDocument(document)}>
                      View document
                    </button>
                  </article>
                ))}
              </div>
            ) : (
              <div className="kyc-document-empty">
                <DocumentIcon />
                <div>
                  <strong>No uploaded document files</strong>
                  <span>This submission contains document details, but the API did not return preview URLs.</span>
                </div>
              </div>
            )}
            <dl className="kyc-details">
              <KycDetail label="National ID" value={viewing.nationalId} document />
              <KycDetail label="Licence number" value={viewing.licenceNumber} document />
              <KycDetail label="Licence expiry" value={viewing.licenceExpiryDate} />
              <KycDetail label="Company name" value={viewing.companyName} />
              <KycDetail label="Company registration" value={viewing.companyRegNumber} document />
            </dl>

            <h3 className="kyc-details-heading">Submission information</h3>
            <dl className="kyc-details">
              <KycDetail label="Phone number" value={viewing.phone} />
              <KycDetail label="Submission ID" value={viewing.id} document />
              <KycDetail label="Submitted at" value={viewing.submittedAt} />
              <KycDetail label="Status" value={viewing.status} />
              <KycDetail label="Reviewed at" value={viewing.reviewedAt} />
              <KycDetail label="Reviewed by" value={viewing.reviewedBy} />
              <KycDetail label="Rejection reason" value={viewing.rejectionReason} />
            </dl>

            <div className="kyc-dialog-actions">
              <button className="btn" type="button" onClick={() => setViewing(null)}>Close</button>
              <button
                className="btn btn-outline"
                disabled={activeRequestId === viewing.id || viewing.status === 'approved'}
                type="button"
                onClick={() => updateRequest(viewing, 'approved')}
              >
                {viewing.status === 'approved' ? 'Approved' : 'Approve'}
              </button>
              <button
                className="btn kyc-reject-btn"
                disabled={activeRequestId === viewing.id || viewing.status === 'rejected'}
                type="button"
                onClick={() => {
                  setViewing(null);
                  setRejecting(viewing);
                }}
              >
                Reject
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {viewingDocument ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setViewingDocument(null)}>
          <section
            aria-labelledby="document-viewer-title"
            aria-modal="true"
            className="signup-dialog kyc-document-viewer"
            role="dialog"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="signup-dialog-head">
              <div>
                <span className="kyc-document-icon" aria-hidden="true"><DocumentIcon /></span>
                <div>
                  <h2 id="document-viewer-title">{viewingDocument.label}</h2>
                  <span>{getDocumentTypeLabel(viewingDocument)}</span>
                </div>
              </div>
              <button className="btn-icon borderless" type="button" aria-label="Close document viewer" onClick={() => setViewingDocument(null)}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <DocumentPreview document={viewingDocument} />
            <div className="kyc-dialog-actions">
              <button className="btn" type="button" onClick={() => setViewingDocument(null)}>Close</button>
              <a className="btn btn-outline" href={viewingDocument.url} rel="noreferrer" target="_blank">Open original</a>
            </div>
          </section>
        </div>
      ) : null}

      {rejecting ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setRejecting(null)}>
          <form
            aria-labelledby="reject-kyc-title"
            aria-modal="true"
            className="signup-dialog kyc-reject-dialog"
            role="dialog"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={(event) => {
              event.preventDefault();
              updateRequest(rejecting, 'rejected', rejectionReason.trim());
            }}
          >
            <div className="signup-dialog-head">
              <div>
                <span className="row-avatar">{rejecting.initials}</span>
                <div>
                  <h2 id="reject-kyc-title">Reject KYC request</h2>
                  <span>{rejecting.name}</span>
                </div>
              </div>
              <button className="btn-icon borderless" type="button" aria-label="Close rejection dialog" onClick={() => setRejecting(null)}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="form-group">
              <label htmlFor="rejection-reason">Reason for rejection</label>
              <textarea
                autoFocus
                id="rejection-reason"
                placeholder="Explain what the applicant needs to correct..."
                required
                value={rejectionReason}
                onChange={(event) => setRejectionReason(event.target.value)}
              />
            </div>
            <div className="kyc-dialog-actions">
              <button className="btn" type="button" onClick={() => setRejecting(null)}>Cancel</button>
              <button className="btn btn-danger" disabled={!rejectionReason.trim() || activeRequestId === rejecting.id} type="submit">
                Reject request
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

function normalizeQueue(response: unknown): KycRecord[] {
  const source = getQueueSource(response);

  return source.map((entry, index) => {
    const applicant = readObject(entry.applicant) ?? readObject(entry.user) ?? {};
    const name = readString(entry, 'applicant_name', 'name', 'full_name')
      ?? readString(applicant, 'name', 'full_name')
      ?? 'Unknown applicant';
    const submittedAt = readString(entry, 'submitted', 'submitted_at', 'created_at');

    return {
      id: readString(entry, 'id', 'request_id', 'kyc_id') ?? String(index),
      userId: readString(entry, 'user_id') ?? 'Not provided',
      initials: readString(entry, 'initials') ?? getInitials(name),
      name,
      phone: readString(entry, 'phone_number', 'phone') ?? 'No phone number',
      role: (readString(entry, 'account_type', 'role', 'requested_role', 'applicant_role') ?? 'driver').replace(/_/g, ' '),
      document: readString(
        entry,
        'licence_number',
        'company_reg_number',
        'national_id',
        'document',
        'document_number',
        'reference',
        'reference_number',
      ) ?? 'Not provided',
      nationalId: readString(entry, 'national_id') ?? 'Not provided',
      licenceNumber: readString(entry, 'licence_number') ?? 'Not provided',
      licenceExpiryDate: formatDate(readString(entry, 'licence_expiry_date')),
      companyName: readString(entry, 'company_name') ?? 'Not provided',
      companyRegNumber: readString(entry, 'company_reg_number') ?? 'Not provided',
      rejectionReason: readString(entry, 'rejection_reason') ?? 'Not provided',
      submitted: formatSubmitted(submittedAt),
      submittedAt: formatDateTime(submittedAt),
      status: (readString(entry, 'status') ?? 'pending').toLowerCase(),
      reviewedAt: formatDateTime(readString(entry, 'reviewed_at')),
      reviewedBy: getReviewerName(entry),
      documents: getDocuments(entry),
    };
  });
}

function DocumentPreview({ document }: { document: KycDocument }) {
  if (isImageDocument(document)) {
    return <img className="kyc-document-preview-image" src={document.url} alt={document.label} />;
  }

  if (isPdfDocument(document)) {
    return <iframe className="kyc-document-preview-frame" src={document.url} title={document.label} />;
  }

  return (
    <div className="kyc-document-preview-unsupported">
      <DocumentIcon />
      <strong>Preview unavailable for this file type</strong>
      <span>Open the original document to view or download it.</span>
    </div>
  );
}

function DocumentIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2h8l4 4v16H6z" />
      <path d="M14 2v5h5M9 13h6M9 17h6" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function getDocuments(entry: Record<string, unknown>): KycDocument[] {
  const documents: KycDocument[] = [];
  const source = entry.documents;

  if (Array.isArray(source)) {
    source.filter(isObject).forEach((document, index) => {
      const url = readString(document, 'url', 'file_url', 'download_url');
      if (url) {
        documents.push({
          id: readString(document, 'id') ?? `document-${index}`,
          label: formatDocumentLabel(readString(document, 'name', 'type', 'document_type') ?? `Document ${index + 1}`),
          url,
          mimeType: readString(document, 'content_type', 'mime_type') ?? inferMimeType(url),
        });
      }
    });
  }

  [
    ['National ID document', 'national_id_url', 'national_id_document_url'],
    ['Driving licence', 'licence_url', 'licence_document_url', 'license_url', 'license_document_url'],
    ['Company registration document', 'company_document_url', 'company_reg_document_url'],
  ].forEach(([label, ...keys]) => {
    const url = readString(entry, ...keys);
    if (url && !documents.some((document) => document.url === url)) {
      documents.push({ id: keys[0], label, url, mimeType: inferMimeType(url) });
    }
  });

  return documents;
}

function getReviewerName(entry: Record<string, unknown>) {
  const directName = readString(entry, 'reviewed_by_full_name', 'reviewer_full_name');
  if (directName) {
    return directName;
  }

  const reviewer = readObject(entry.reviewed_by) ?? readObject(entry.reviewer);
  const nestedName = reviewer ? readString(reviewer, 'full_name', 'name', 'username') : undefined;
  if (nestedName) {
    return nestedName;
  }

  const reviewedBy = readString(entry, 'reviewed_by');
  if (!reviewedBy) {
    return 'Not reviewed';
  }

  if (!isUuid(reviewedBy)) {
    return reviewedBy;
  }

  return 'Reviewer name unavailable';
}

function isUuid(value: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function formatDocumentLabel(value: string) {
  return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function inferMimeType(url: string) {
  const extension = url.split('?')[0].split('.').pop()?.toLowerCase();
  if (extension === 'pdf') return 'application/pdf';
  if (extension === 'png') return 'image/png';
  if (extension === 'webp') return 'image/webp';
  if (extension === 'jpg' || extension === 'jpeg') return 'image/jpeg';
  return '';
}

function isImageDocument(document: KycDocument) {
  return document.mimeType.startsWith('image/') || /\.(png|jpe?g|webp|gif)(?:\?|$)/i.test(document.url);
}

function isPdfDocument(document: KycDocument) {
  return document.mimeType === 'application/pdf' || /\.pdf(?:\?|$)/i.test(document.url);
}

function getDocumentTypeLabel(document: KycDocument) {
  if (isPdfDocument(document)) return 'PDF document';
  if (isImageDocument(document)) return 'Image document';
  return document.mimeType || 'Document file';
}

function KycDetail({ document = false, label, value }: { document?: boolean; label: string; value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd className={document ? 'kyc-document' : undefined}>{value}</dd>
    </div>
  );
}

function getStatusTone(status: string) {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'danger';
  return 'warn';
}

function createDemoRecord(id: string, name: string, phone: string, role: string, document: string, submitted: string): KycRecord {
  return {
    id,
    userId: `user-${id}`,
    initials: getInitials(name),
    name,
    phone,
    role,
    document,
    nationalId: '12345678901',
    licenceNumber: role === 'driver' ? document : 'Not provided',
    licenceExpiryDate: role === 'driver' ? 'December 31, 2028' : 'Not provided',
    companyName: role === 'fleet operator' ? `${name.split(' ')[0]} Transport Ltd` : 'Not provided',
    companyRegNumber: role === 'fleet operator' ? document : 'Not provided',
    rejectionReason: 'Not provided',
    submitted,
    submittedAt: submitted,
    status: 'pending',
    reviewedAt: 'Not reviewed',
    reviewedBy: 'Not reviewed',
    documents: [],
  };
}

function getQueueSource(response: unknown): Record<string, unknown>[] {
  if (Array.isArray(response)) {
    return response.filter(isObject);
  }

  if (!isObject(response)) {
    return [];
  }

  for (const key of ['submissions', 'items', 'requests', 'results', 'kyc_requests', 'data']) {
    const value = response[key];
    if (Array.isArray(value)) {
      return value.filter(isObject);
    }
  }

  return [];
}

function formatSubmitted(value: string | undefined) {
  if (!value) {
    return 'Unknown';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  if (seconds < 172800) return 'yesterday';
  return date.toLocaleDateString();
}

function formatDate(value: string | undefined) {
  if (!value) {
    return 'Not provided';
  }

  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatDateTime(value: string | undefined) {
  if (!value) {
    return 'Not provided';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function getInitials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'NA';
}

function readString(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value) {
      return value;
    }
  }
  return undefined;
}

function readObject(value: unknown) {
  return isObject(value) ? value : undefined;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function publishPendingCount(queue: KycRecord[]) {
  const pendingCount = queue.filter((item) => item.status === 'pending').length;
  window.dispatchEvent(new CustomEvent(KYC_PENDING_COUNT_EVENT, { detail: pendingCount }));
}
